import React from 'react';
import { InboxIcon, SearchIcon } from 'lucide-react';
export interface FilterChip {
  id: string;
  label: string;
  count?: number;
}
interface ListPageLayoutProps {
  title: string;
  subtitle: string;
  actions?: React.ReactNode;
  banner?: React.ReactNode;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  filters: FilterChip[];
  activeFilter: string;
  onFilterChange: (id: string) => void;
  loading: boolean;
  isEmpty: boolean;
  emptyTitle: string;
  emptyMessage: string;
  resultLabel: string;
  children: React.ReactNode;
}
export function ListPageLayout({
  title,
  subtitle,
  actions,
  banner,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filters,
  activeFilter,
  onFilterChange,
  loading,
  isEmpty,
  emptyTitle,
  emptyMessage,
  resultLabel,
  children
}: ListPageLayoutProps) {
  return <main className="w-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8" data-id="element-310" data-mp="21sheqz06nnma">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4" data-id="element-311" data-mp="26o6a0q566ics">
        <header className="flex flex-wrap items-start justify-between gap-3" data-id="element-312" data-mp="2ow3u5u3eisvy">
          <div data-id="element-313" data-mp="2kv1td8tu68rv">
            <h1 className="text-2xl font-bold tracking-tight text-navy-900" data-id="element-314" data-mp="2kvl0rvlv1ern">
              {title}
            </h1>
            <p className="mt-1 text-sm text-navy-500" data-id="element-315" data-mp="2ytrrmrse6eht">{subtitle}</p>
          </div>
          {actions ? <div className="flex gap-2" data-id="element-316" data-mp="2tvgiykdu2a16">{actions}</div> : null}
        </header>

        {banner}

        <div className="rounded-2xl border border-navy-100 bg-white shadow-card" data-id="element-317" data-mp="265dt8axe5g5q">
          <div className="flex flex-col gap-3 border-b border-navy-100 p-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-318" data-mp="217caxs7shkvb">
            <div className="relative w-full lg:max-w-xs" data-id="element-319" data-mp="2fx0bgr85cfhz">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-400" aria-hidden="true" data-id="element-320" data-mp="2b58ika1ipxoo" />
              <label htmlFor="list-search" className="sr-only" data-id="element-321" data-mp="1ndb7wm9t1bp5">
                {searchPlaceholder}
              </label>
              <input id="list-search" type="search" value={searchValue} onChange={event => onSearchChange(event.target.value)} placeholder={searchPlaceholder} className="h-11 w-full rounded-lg border border-navy-200 pl-9 pr-3 text-sm text-navy-900 placeholder:text-navy-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-322" data-mp="3ij2fhgovtryx" />
            </div>

            <div role="group" aria-label="Filter list" className="-mx-1 flex flex-wrap gap-2 px-1" data-id="element-323" data-mp="2zmayjdwtboy1">
              {filters.map(filter => {
              const active = filter.id === activeFilter;
              return <button key={filter.id} type="button" onClick={() => onFilterChange(filter.id)} aria-pressed={active} className={`inline-flex h-10 items-center gap-2 rounded-full border pl-3.5 text-sm font-semibold transition-colors ${typeof filter.count === 'number' ? 'pr-1.5' : 'pr-3.5'} ${active ? 'border-navy-800 bg-navy-800 text-white' : 'border-navy-200 text-navy-700 hover:border-navy-300 hover:bg-navy-50'}`} data-id="element-324" data-mp="3dhmu1nisljg0">
                    {filter.label}
                    {typeof filter.count === 'number' ? <span className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums ${active ? 'bg-white/15 text-white' : 'bg-navy-100 text-navy-600'}`} data-id="element-325" data-mp="f735lijo56sa">
                        {filter.count}
                      </span> : null}
                  </button>;
            })}
            </div>
          </div>

          {loading ? <ul className="divide-y divide-navy-100" aria-busy="true" data-id="element-326" data-mp="2cmfgvadi58gm">
              {[0, 1, 2, 3, 4].map(index => <li key={index} className="p-4" data-id="element-327" data-mp="1ynfwiex0vfq9">
                  <div className="h-4 w-1/3 animate-pulse rounded bg-navy-100" data-id="element-328" data-mp="22ornoovjo90k" />
                  <div className="mt-2.5 h-3 w-2/3 animate-pulse rounded bg-navy-50" data-id="element-329" data-mp="1oxwe1ad81nvx" />
                </li>)}
            </ul> : isEmpty ? <div className="p-10 text-center" data-id="element-330" data-mp="1tpxkgfnpkke1">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-navy-50 ring-1 ring-navy-100" data-id="element-331" data-mp="1f08gkco8e2a2">
                <InboxIcon className="h-5 w-5 text-navy-400" aria-hidden="true" data-id="element-332" data-mp="1jzl2mv7a35bl" />
              </span>
              <h2 className="mt-3 text-sm font-semibold text-navy-800" data-id="element-333" data-mp="1ay50tbuj4biz">
                {emptyTitle}
              </h2>
              <p className="mx-auto mt-1 max-w-sm text-sm text-navy-500" data-id="element-334" data-mp="rbtgaee7jdt8">
                {emptyMessage}
              </p>
            </div> : <>
              <p className="sr-only" aria-live="polite" data-id="element-335" data-mp="10d1det4zm8lq">
                {resultLabel}
              </p>
              {children}
            </>}
        </div>

        {!loading && !isEmpty ? <p className="text-xs text-navy-500" data-id="element-336" data-mp="1ab7dd578h4ny">{resultLabel}</p> : null}
      </div>
    </main>;
}